import { useCallback, useEffect } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { isTrackingAllowed } from "@app/utils/tracking"

// Insights caps objectIDs at 20 per event
const MAX_OBJECT_IDS_PER_EVENT = 20

type InsightsEvent = Record<string, any>

type AlgoliaInsightsConfig = {
  config: {
    applicationId?: string
    searchOnlyApiKey?: string
  }
}

// Recommendation products are stamped with these fields by the useAlgolia* hooks,
// linking Insights events back to the recommend/search request that surfaced them
type AlgoliaTrackedItem = {
  queryID?: string
  objectID?: string
  indexName?: string
  position?: number
}

const sendAlgoliaInsightsEvents = async (applicationId: string | undefined, apiKey: string | undefined, events: InsightsEvent[]) => {
  if (typeof window === "undefined") return
  if (!isTrackingAllowed()) return
  const userToken = localStorage.getItem("algolia_user_token")
  if (!applicationId || !apiKey || !userToken || !events.length) return

  try {
    await fetch("https://insights.algolia.io/1/events", {
      method: "POST",
      headers: {
        "X-Algolia-Application-Id": applicationId,
        "X-Algolia-API-Key": apiKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        events: events.map(event => ({
          ...event,
          userToken,
        })),
      }),
      // Let the request outlive full-page navigations (e.g. checkout redirect after add-to-cart)
      keepalive: true,
    })
  } catch (error) {
    console.error("Error sending Insights event:", error)
  }
}

const useAlgoliaInsights = () => {
  const { config } = useStaticQuery<GatsbyTypes.StaticAlgoliaInsightsQuery>(graphql`
    query StaticAlgoliaInsights {
      config: sanitySettingAlgolia {
        applicationId
        searchOnlyApiKey
      }
    }
  `)

  const sendEvents = useCallback(
    (events: InsightsEvent[]) => sendAlgoliaInsightsEvents(config?.applicationId, config?.searchOnlyApiKey, events),
    [config?.applicationId, config?.searchOnlyApiKey]
  )

  // Click on a product surfaced by a recommendation widget. No-ops for items that
  // weren't stamped by a recommend hook (e.g. search results track their own clicks)
  const trackRecommendedProductClick = useCallback(
    (item: AlgoliaTrackedItem) => {
      if (!item?.queryID || !item?.objectID || !item?.indexName) return

      sendEvents([
        {
          eventType: "click",
          eventName: "Recommended Product Clicked",
          index: item.indexName,
          queryID: item.queryID,
          objectIDs: [item.objectID],
          positions: [item.position ?? 1],
        },
      ])
    },
    [sendEvents]
  )

  // Impression event for the recommended products actually rendered by a widget
  const trackRecommendationsViewed = useCallback(
    (items: AlgoliaTrackedItem[]) => {
      const viewable = (items ?? []).filter(item => item?.objectID && item?.indexName)
      if (!viewable.length) return

      sendEvents([
        {
          eventType: "view",
          eventName: "Recommendations Viewed",
          index: viewable[0].indexName,
          objectIDs: viewable.slice(0, MAX_OBJECT_IDS_PER_EVENT).map(item => item.objectID),
        },
      ])
    },
    [sendEvents]
  )

  return { sendEvents, trackRecommendedProductClick, trackRecommendationsViewed }
}

// Sends one impression event per distinct set of rendered recommendations, keyed on
// objectIDs so re-renders with the same products don't re-fire
const useTrackRecommendationsViewed = (items: AlgoliaTrackedItem[]) => {
  const { trackRecommendationsViewed } = useAlgoliaInsights()
  const key = (items ?? [])
    .map(item => item?.objectID)
    .filter(Boolean)
    .join("|")

  useEffect(() => {
    if (key) trackRecommendationsViewed(items)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}

export { useAlgoliaInsights, useTrackRecommendationsViewed, sendAlgoliaInsightsEvents, MAX_OBJECT_IDS_PER_EVENT }
export type { AlgoliaTrackedItem }
